import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";

function glow(color: string, strong = false): string {
  return strong
    ? `0 0 20px ${color}99, 0 0 50px ${color}50, 0 0 80px ${color}20`
    : `0 0 12px ${color}60, 0 0 30px ${color}25`;
}

const LEFT_ITEMS = [
  "Visual builder",
  "Drag widgets",
  "Connect to data",
  "Deploy app",
  "Requires 'citizen developer' skills",
];

const RIGHT_ITEMS = [
  "Describe in English",
  "EAKC builds tools",
  "Describe workflow",
  "EAKC chains tools",
  "Grounded in YOUR data",
];

export const Scene10Phase5: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  /* Exit fade */
  const fadeOut = interpolate(frame, [240, 270], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  /* Title */
  const titleSp = spring({ frame, fps, config: SPRING.snappy });
  const titleOp = interpolate(titleSp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const titleY  = interpolate(titleSp, [0, 1], [-40, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  /* Subtitle */
  const subSp = spring({ frame: frame - 10, fps, config: SPRING.gentle });
  const subOp = interpolate(subSp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  /* VS divider */
  const vsSp  = spring({ frame: frame - 20, fps, config: SPRING.bouncy });
  const vsOp  = interpolate(vsSp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const vsScale = interpolate(vsSp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const vsPulse = 1 + Math.sin(frame * 0.12) * 0.08;

  /* Bottom statement */
  const stmtSp = spring({ frame: frame - 180, fps, config: SPRING.punch });
  const stmtOp  = interpolate(stmtSp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const stmtScale = interpolate(stmtSp, [0, 1], [0.7, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  const colStart = 30;
  const colGap   = 15;

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fadeOut }}>
      {/* Split background tint */}
      <div style={{
        position: "absolute", inset: 0,
        background: `linear-gradient(90deg, ${COLORS.neonRed}06 0%, transparent 48%, transparent 52%, ${COLORS.neonGreen}06 100%)`,
      }} />

      {/* Grid */}
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: `linear-gradient(${COLORS.neonPink}06 1px, transparent 1px), linear-gradient(90deg, ${COLORS.neonPink}06 1px, transparent 1px)`,
        backgroundSize: "60px 60px",
      }} />

      {/* Title */}
      <div style={{
        position: "absolute", top: 36, left: "50%",
        transform: `translateX(-50%) translateY(${titleY}px)`,
        opacity: titleOp, textAlign: "center",
      }}>
        <div style={{
          fontSize: 44,
          fontFamily: FONTS.heading,
          fontWeight: 800,
          color: COLORS.neonPink,
          textShadow: glow(COLORS.neonPink, true),
          letterSpacing: -1,
        }}>
          Phase 5: Full Autonomy
        </div>
      </div>

      {/* Subtitle */}
      <div style={{
        position: "absolute", top: 100, left: "50%",
        transform: "translateX(-50%)",
        opacity: subOp, textAlign: "center",
      }}>
        <div style={{
          fontSize: 28,
          fontFamily: FONTS.heading,
          fontWeight: 700,
          color: COLORS.neonYellow,
          textShadow: glow(COLORS.neonYellow),
          letterSpacing: 2,
        }}>
          EAKC Power Apps
        </div>
      </div>

      {/* Left column — Microsoft */}
      <div style={{
        position: "absolute",
        top: 180, left: 80,
        width: 740,
        transform: "perspective(900px) rotateY(5deg)",
      }}>
        <div style={{
          background: `linear-gradient(135deg, ${COLORS.neonRed}12, ${COLORS.bgCard}cc)`,
          border: `1.5px solid ${COLORS.neonRed}40`,
          borderRadius: 18,
          padding: "40px 48px",
          boxShadow: `0 0 30px ${COLORS.neonRed}20`,
        }}>
          <div style={{
            fontSize: 26,
            fontFamily: FONTS.heading,
            fontWeight: 700,
            color: COLORS.neonRed,
            marginBottom: 30,
            letterSpacing: 1,
            textTransform: "uppercase",
            textShadow: glow(COLORS.neonRed),
          }}>
            Microsoft Power Apps
          </div>
          {LEFT_ITEMS.map((item, i) => {
            const sp = spring({ frame: frame - (colStart + i * colGap), fps, config: SPRING.bouncy });
            const op = interpolate(sp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
            const x  = interpolate(sp, [0, 1], [-30, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
            const isLast = i === LEFT_ITEMS.length - 1;
            return (
              <div key={item} style={{
                opacity: op,
                transform: `translateX(${x}px)`,
                marginBottom: 22,
                display: "flex",
                alignItems: "center",
                gap: 16,
              }}>
                <div style={{
                  width: 10, height: 10, borderRadius: "50%",
                  background: isLast ? COLORS.neonRed : COLORS.textMuted,
                  flexShrink: 0,
                  boxShadow: isLast ? glow(COLORS.neonRed) : "none",
                }} />
                <div style={{
                  fontSize: 24,
                  fontFamily: FONTS.body,
                  color: isLast ? COLORS.neonRed : COLORS.textSecondary,
                  fontWeight: isLast ? 700 : 400,
                  textShadow: isLast ? glow(COLORS.neonRed) : "none",
                }}>
                  {item}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Right column — EAKC */}
      <div style={{
        position: "absolute",
        top: 180, right: 80,
        width: 740,
        transform: "perspective(900px) rotateY(-5deg)",
      }}>
        <div style={{
          background: `linear-gradient(135deg, ${COLORS.neonGreen}12, ${COLORS.bgCard}cc)`,
          border: `1.5px solid ${COLORS.neonGreen}50`,
          borderRadius: 18,
          padding: "40px 48px",
          boxShadow: `0 0 30px ${COLORS.neonGreen}25, 0 0 60px ${COLORS.neonGreen}10`,
        }}>
          <div style={{
            fontSize: 26,
            fontFamily: FONTS.heading,
            fontWeight: 700,
            color: COLORS.neonGreen,
            marginBottom: 30,
            letterSpacing: 1,
            textTransform: "uppercase",
            textShadow: glow(COLORS.neonGreen),
          }}>
            EAKC Power Apps
          </div>
          {RIGHT_ITEMS.map((item, i) => {
            const sp = spring({ frame: frame - (colStart + i * colGap), fps, config: SPRING.bouncy });
            const op = interpolate(sp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
            const x  = interpolate(sp, [0, 1], [30, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
            const isLast = i === RIGHT_ITEMS.length - 1;
            return (
              <div key={item} style={{
                opacity: op,
                transform: `translateX(${x}px)`,
                marginBottom: 22,
                display: "flex",
                alignItems: "center",
                gap: 16,
              }}>
                <div style={{
                  width: 10, height: 10, borderRadius: "50%",
                  background: isLast ? COLORS.neonGreen : COLORS.textMuted,
                  flexShrink: 0,
                  boxShadow: isLast ? glow(COLORS.neonGreen, true) : "none",
                }} />
                <div style={{
                  fontSize: 24,
                  fontFamily: FONTS.body,
                  color: isLast ? COLORS.neonGreen : COLORS.textSecondary,
                  fontWeight: isLast ? 700 : 400,
                  textShadow: isLast ? glow(COLORS.neonGreen, true) : "none",
                }}>
                  {item}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Center VS divider */}
      <div style={{
        position: "absolute",
        top: 180, left: "50%",
        transform: `translateX(-50%)`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        height: 540,
        opacity: vsOp,
      }}>
        {/* Top line */}
        <div style={{
          width: 2,
          flex: 1,
          background: `linear-gradient(to bottom, transparent, ${COLORS.neonYellow}80)`,
        }} />
        {/* VS badge */}
        <div style={{
          transform: `scale(${vsScale * vsPulse})`,
          background: `${COLORS.neonYellow}20`,
          border: `3px solid ${COLORS.neonYellow}`,
          borderRadius: "50%",
          width: 80,
          height: 80,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 26,
          fontFamily: FONTS.heading,
          fontWeight: 900,
          color: COLORS.neonYellow,
          boxShadow: glow(COLORS.neonYellow, true),
          flexShrink: 0,
        }}>
          VS
        </div>
        {/* Bottom line */}
        <div style={{
          width: 2,
          flex: 1,
          background: `linear-gradient(to bottom, ${COLORS.neonYellow}80, transparent)`,
        }} />
      </div>

      {/* Bottom statement */}
      <div style={{
        position: "absolute",
        bottom: 120,
        left: "50%",
        transform: `translateX(-50%) scale(${stmtScale})`,
        opacity: stmtOp,
        textAlign: "center",
        width: 1500,
      }}>
        <div style={{
          fontSize: 40,
          fontFamily: FONTS.heading,
          fontWeight: 800,
          background: `linear-gradient(90deg, ${COLORS.neonPink}, ${COLORS.neonPurple})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
          textShadow: "none",
          filter: `drop-shadow(0 0 20px ${COLORS.neonPink}60)`,
          lineHeight: 1.4,
          letterSpacing: 0.3,
        }}>
          The user doesn't build. The user describes. EAKC handles everything.
        </div>
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
